import { atom } from "jotai";
import dayjs from "dayjs";
import {
  AppSettingData,
  AppSettingInitial,
} from "../../../stores/appSettingStore";
import {
  MimosysDataAscAtom,
  MimosysDataAtom,
  MimosysDataInitial,
  latestMentalActivityAtom,
  latestVitalityAtom,
  ResultChartData,
} from "../../../stores/mimosysStore";
import { weekDataExtractor } from "../../../utils/mimosysDataExtracter";
import { allScoreAverage, sdCalc } from "../../../utils/mimosysDataStatistics";
import { Trend } from "../../../types";

/**
 * ホーム画面のロジックと状態
 */

export const recordDialogOpenAtom = atom<boolean>(false);
export const showSessionTimeoutAtom = atom(false);
export const homeSplashDisplayAtom = atom(true);

export const RecordCountAtom = atom({ count: 0 });

export const homeWeekDataAtom = atom<ResultChartData[]>((get) => {
  const now = dayjs();
  return weekDataExtractor(now, get(MimosysDataAscAtom), 7);
});

export const scoreAverageAtom = atom((get) => {
  return allScoreAverage(get(MimosysDataAscAtom)).map((value) => {
    return value;
  });
});

const scoreSD = atom((get) => {
  const [vitalityAve, mentalActivityAve] = get(scoreAverageAtom);
  const mimosysData = get(MimosysDataAscAtom);
  return sdCalc(mimosysData, vitalityAve * 100, mentalActivityAve * 100);
});

export const vitalitySdSetAtom = atom((get) => {
  const [vitalitySd] = get(scoreSD);
  const sdChartValue = get(AppSettingData).sd_chart;
  const [vitalityAve] = get(scoreAverageAtom);
  const vitalitySdTop = Math.min(
    100,
    vitalityAve * 100 + sdChartValue * vitalitySd,
  );
  const vitalitySdBottom = Math.max(
    0,
    vitalityAve * 100 - sdChartValue * vitalitySd,
  );
  return [vitalitySdTop, vitalitySdBottom];
});

export const mentalActivitySdSetAtom = atom((get) => {
  const [, mentalActivitySd] = get(scoreSD);
  const sdChartValue = get(AppSettingData).sd_chart;
  const [, mentalActivityAve] = get(scoreAverageAtom);
  const mentalActivitySdTop = Math.min(
    100,
    mentalActivityAve * 100 + sdChartValue * mentalActivitySd,
  );
  const mentalActivitySdBottom = Math.max(
    0,
    mentalActivityAve * 100 - sdChartValue * mentalActivitySd,
  );
  return [mentalActivitySdTop, mentalActivitySdBottom];
});

export const mi1NormalRange = [8, 3];

export const latestVitalityTrendAtom = atom((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  if (mimosysData.length > 1) {
    const latestVitality = get(latestVitalityAtom) * 100;
    const [vitalitySdTop, vitalitySdBottom] = get(vitalitySdSetAtom);
    if (latestVitality > vitalitySdTop) {
      return Trend.Up;
    } else if (latestVitality < vitalitySdBottom) {
      return Trend.Down;
    } else {
      return Trend.Normal;
    }
  } else {
    return Trend.Normal;
  }
});

export const latestMentalActivityTrendAtom = atom((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  if (mimosysData.length > 1) {
    const latestMentalActivity = get(latestMentalActivityAtom) * 100;
    const [mentalActivitySdTop, mentalActivitySdBottom] = get(
      mentalActivitySdSetAtom,
    );
    if (latestMentalActivity > mentalActivitySdTop) {
      return Trend.Up;
    } else if (latestMentalActivity < mentalActivitySdBottom) {
      return Trend.Down;
    } else {
      return Trend.Normal;
    }
  } else {
    return Trend.Normal;
  }
});

export const recordingLastDatesAtom = atom((get) => {
  let lastCount = 0;
  const checkDate = dayjs();
  const mimosysData = JSON.parse(JSON.stringify(get(MimosysDataAscAtom)));
  mimosysData.reverse();
  for (const data of mimosysData) {
    if (checkDate.isSame(dayjs(data.recorded_at), "d")) {
      lastCount += 1;
      checkDate.add(-1, "d");
    } else if (
      checkDate.clone().add(1, "d").isSame(dayjs(data.recorded_at), "day")
    ) {
      console.log("skip");
    } else {
      break;
    }
  }
  return lastCount;
});

export const logoutAtom = atom(null, (get, set) => {
  set(MimosysDataAtom, MimosysDataInitial);
  set(AppSettingData, AppSettingInitial);
  set(homeSplashDisplayAtom, true);
  set(showSessionTimeoutAtom, false);
  set(recordDialogOpenAtom, false);
});
